import React, {
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState
} from 'react';
import moment from 'moment';
import appConfig from 'app-config';
import { Tooltip } from '@jutro/components';
import { MetadataContent } from '@jutro/legacy/uiconfig';
import { useTranslator } from '@jutro/locale';
import { ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { UserAccountService, LoadSaveService } from 'gw-capability-quoteandbind';
import _ from 'lodash';
import { wizardProps } from '@xengage/gw-portals-wizard-react';
import { MLNotificationBlockUtil } from 'ml-portals-util-react';
import { MLTagManagerService , MLModalService } from 'ml-portals-services-react'
import { platformMessages } from 'ml-components-platform-react';
import { MLAlertGroupComponent } from 'ml-components-common-react';
import {
    MLQuoteNameUtil, MLProductCodeUtil, MLLocalDateUtil, convertToSentenceCase
} from 'ml-portals-util-js';
import PATrackEvent from '../../../gw-capability-gateway-quoteandbind-common-react/components/TrackEvent/PATrackEvent';
import HOTrackEvent from '../../../gw-capability-gateway-quoteandbind-common-react/components/TrackEvent/HOTrackEvent';
import PUMBTrackEvent from '../../../gw-capability-gateway-quoteandbind-common-react/components/TrackEvent/PUMBTrackEvent';
import metadata from './ConfirmationPage.metadata.json5';
import messages from './ConfirmationPage.messages';
import styles from './ConfirmationPage.module.scss';
import links from '../../../../../quote-and-buy/src/config/ml-links.json';

import { IntlContext } from '@jutro/legacy/locale';

function NamedNonOwnerTooltipComponent() {
    const translator = useTranslator();
    return (
        <Tooltip
            id="namedNonOwnerTooltip"
            placement="bottom"
            trigger="click"
            content={translator(messages.confirmationNamedNonOwnerTooltipContent)}
        >
            <span className={styles.namedNonOwnerTooltip}>
                <i className="mir mi-help-outline" />
            </span>
        </Tooltip>
    );
}

function ConfirmationPage(props) {
    const { wizardData: submissionVM, updateWizardData } = props;
    const translator = useTranslator();
    const viewModelService = useContext(ViewModelServiceContext);
    const intl = useContext(IntlContext);

    const CARCO_CONTINGENCY_TITLE = 'Vehicle Inspection Required';
    const {
        ML_CARCO_FIND_LOCATION_URL
    } = appConfig;

    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
    const [downPaymentMethod, setDownPaymentMethod] = useState(null);
    const [downPaymentLast4Digits, setDownPaymentLast4Digits] = useState(null);
    const [last4Digits, setLast4Digits] = useState(null);

    const baseData = _.get(submissionVM, 'baseData.value', {});
    const bindData = _.get(submissionVM, 'bindData.value', {});
    const openContingencies = _.get(submissionVM, 'bindData.openContingencies_ML.value', []);
    const primaryNamedInsured = _.get(submissionVM, 'baseData.primaryNamedInsured_ML.value', {});
    const coInsured = _.get(submissionVM, 'baseData.secondaryNamedInsured_ML.value', undefined);
    const policyNumber = _.get(submissionVM, 'bindData.policyNumber.value', '');
    const email = _.get(primaryNamedInsured, 'emailAddress1', '');
    const phoneNumber = _.get(primaryNamedInsured, 'homeNumber', '');
    const dob = MLLocalDateUtil.localDate2Date(primaryNamedInsured.dateOfBirth);
    const paymentDetails = _.get(bindData, 'paymentDetails');
    const paymentTotal = paymentDetails.amountCharged_ML;
    const paymentReceivedDate = moment(paymentDetails.paymentReceivedDate_ML).format('MM/DD/YYYY');
    const confirmationNumber = paymentDetails.confirmationNumber_ML;
    const policyZipCode = baseData.policyAddress.postalCode;
    const hasStoredCSSHomeRef = useRef(false);
    const isQnB = appConfig.portalCode === 'QB';

    const {
        addressLine1,
        addressLine2,
        city,
        postalCode,
        state
    } = primaryNamedInsured.primaryAddress;

    const {
        showProgressDialogTwoPromises
    } = MLModalService();

    const {
        addErrorAlert,
        addSuccessAlert,
        clearBlockAlerts
    } = MLNotificationBlockUtil();

    const {
        state: propertyAddressState,
        addressLine1: propertyAddressLine1,
        addressLine2: propertyAddressLine2,
        city: propertyAddressCity,
        postalCode: propertyAddressZip
    } = baseData.policyAddress;

    const personalAuto = _.get(submissionVM, 'lobData.personalAuto', undefined);
    const vehicles = _.get(personalAuto, 'coverables.vehicles.value', []);
    const isNamedNonOwner = !_.isUndefined(personalAuto.value) ? vehicles.some((vehicle) => {
        return vehicle.namedNonOwner_ML === true;
    }) : false;

    const userAgentStringML = window.navigator.userAgent;
    const isUserAgentSafariML = userAgentStringML.match('^(?!.*(?:Chrome|Edge)).*Safari');

    const getFullName = (person) => {
        const nameSuffixList = viewModelService.productMetadata.get('pc').types.getTypelist('NameSuffix');
        const middleName = (person.middleName) ? person.middleName : '';
        const suffixTypeKeyName = (person.suffix) ? nameSuffixList.getCode(person.suffix).name : '';
        const suffixDisplayName = translator(
            {
                id: suffixTypeKeyName,
                defaultMessage: ' '
            }
        );
        return `${person.firstName} ${middleName} ${person.lastName} ${suffixDisplayName}`;
    };

    const primaryNamedInsuredFullName = getFullName(primaryNamedInsured);
    const coInsuredFullName = coInsured ? getFullName(coInsured) : undefined;

    const getProductName = useCallback(() => {
        return MLQuoteNameUtil.getProductName(baseData.productCode);
    }, [baseData.productCode]);

    const showErrorMessage = useCallback(() => {
        clearBlockAlerts('experiencingTechnicalIssues')
        addErrorAlert(messages.registrationTechnicalIssues, 'experiencingTechnicalIssues');
    }, [addErrorAlert, clearBlockAlerts]);

    useEffect(() => {
        const productName = getProductName();
        const successMsg = {...messages.congratsPolicy, args: { productName: productName }};
        const alertName = `SuccessfulPolicy${productName}`;

        // EventHub.publish('journey', 'purchase', ctrl.createPurchaseEvent());
        // clearBlockAlerts(alertName);
        addSuccessAlert(successMsg, alertName);

        if (submissionVM.baseData.productCode.value === 'HOPHomeowners') {
            HOTrackEvent('Issuance Step Reached', false, submissionVM, null, { step: 'Confirmation ePage'}, true);
        }
        else if (submissionVM.baseData.productCode.value === 'PersonalUmbrella') {
            PUMBTrackEvent('Issuance Step Reached', false, submissionVM, null, { step: 'Confirmation Page'}, true);
        }
        else {
            PATrackEvent('Issuance Step Reached', false, submissionVM, null, { step: 'Confirmation Page' }, true);
        }
    }, []);


    useEffect(() => {
        if (hasStoredCSSHomeRef.current) return;

        const storageKey = 'CSSHomeUrl';
        const CSSHomeUrlValue = _.get(submissionVM, 'bindData.cssHomeUrl.value', '');

        if (_.isString(CSSHomeUrlValue) && !_.isEmpty(CSSHomeUrlValue.trim())) {
            sessionStorage.setItem(storageKey, CSSHomeUrlValue);
            hasStoredCSSHomeRef.current = true;
        }

    }, [submissionVM]);

    const generateIntervalSequence = (retryTime) => {
        const sequence = [1, 1];
        for (let i = 2; i < retryTime; i += 1) {
            sequence.push(sequence[i - 1] + sequence[i - 2]);
        }
        return sequence;
    };

    const goToContactUs = () => {
        const contactUsPath = 'quote-and-buy/contact-us';
        const url = `${window.location.origin}/${contactUsPath}`;
        window.location.href = url;
    };

    const getPaymentMethodDisplayKey = useCallback((paymentInstruments, isDownPayment) => {
        const paymentInstrument = paymentInstruments.find((instrument) => {
            if (isDownPayment) {
                return instrument.token === paymentDetails.downPaymentInstrument_ML;
            }
            return instrument.token === paymentDetails.selectedPaymentInstrument_ML;
        });

        if (isDownPayment) {
            const paymentMethod = _.get(paymentInstrument, 'paymentMethod');
            switch (paymentMethod) {
                case 'ach':
                    setDownPaymentLast4Digits(paymentInstrument ? paymentInstrument.last4Digits : '');
                    return translator(messages.bankAccountPaymentMethod);
                case 'creditcard':
                    setDownPaymentLast4Digits(paymentInstrument ? paymentInstrument.last4Digits : '');
                    return translator(messages.cardPaymentMethod);
                default:
                    console.error(new Error('No payment method specified'));
            }
        }

        switch (paymentDetails.ratingPaymentMethod_ML) {
            case 'credit_debit_card':
                setLast4Digits(paymentDetails.selectedPaymentInstrument_ML ? paymentDetails.last4Digits_ML : '');
                return translator(messages.cardPaymentMethod);
            case 'bank_account':
                setLast4Digits(paymentInstrument ? paymentInstrument.last4Digits : '');
                return translator(messages.bankAccountPaymentMethod);
            case 'mortgagee':
                return translator(messages.billMortgageedPaymentMethod);
            case 'check':
                return translator(messages.onlineOrMailPaymentMethod);
            case 'payroll_deduction':
                return translator(messages.payrollDeductionPaymentMethod);
            default:
                console.error(new Error('No payment method specified'));
        }
    }, [
        paymentDetails.downPaymentInstrument_ML,
        paymentDetails.last4Digits_ML,
        paymentDetails.ratingPaymentMethod_ML,
        paymentDetails.selectedPaymentInstrument_ML,
        translator
    ]);


    const triggerPurchaseEvent = useCallback((_selectedPaymentMethod) => {
        const attributes = {
            product: baseData.productCode,
            productAmount: paymentTotal.amount,
            productMethod: _selectedPaymentMethod,
            confirmationID: confirmationNumber,
            policyNumber: policyNumber
        };
        MLTagManagerService.sendEvent('purchasePolicy', attributes);
    }, [baseData.productCode, confirmationNumber, paymentTotal.amount, policyNumber]);

    const initSelectedRatingPaymentMethod = useCallback(async () => {
        const quoteID = _.get(submissionVM, 'quoteID.value', '');
        const sessionUUID = _.get(submissionVM, 'sessionUUID.value', '');
        const paymentInstrumentPromise = LoadSaveService.getPaymentInstruments(quoteID, sessionUUID);

        paymentInstrumentPromise.then((instruments) => {
            setSelectedPaymentMethod(getPaymentMethodDisplayKey(instruments, false));
            setDownPaymentMethod(getPaymentMethodDisplayKey(instruments, true));
            triggerPurchaseEvent(getPaymentMethodDisplayKey(instruments, false));
        }, (err) => {
            setSelectedPaymentMethod(getPaymentMethodDisplayKey([], false));
            setDownPaymentMethod(getPaymentMethodDisplayKey([], true));
            triggerPurchaseEvent(getPaymentMethodDisplayKey([], false));
        }).finally(() => {
        });
        return paymentInstrumentPromise;
    }, [getPaymentMethodDisplayKey, submissionVM, triggerPurchaseEvent]);

    /**
     * Retrieve a submission if the object is not yet 'bound' status. Once it's been bound,
     * it should be able to show all post-bound data such as contingencies.
     *
     * @param {Object} tmpSubmission
     * @returns {*}
     * @private
    */
    const updateSubmisionDataIfNotBound = useCallback(async (tmpSubmission) => {
        const quoteRetrievalDTO = {
            postalCode: tmpSubmission.baseData.policyAddress.postalCode.value.substring(0, 5),
            quoteID: tmpSubmission.quoteID.value
        };

        const retryCount = 30;

        let counter = 0;

        const timeoutSequence = generateIntervalSequence(retryCount);

        const promise = new Promise((resolve, reject) => {
            const isPolicyBound = () => {
                LoadSaveService.isPeriodBound(quoteRetrievalDTO).then((isBound) => {
                    // eslint-disable-next-line no-use-before-define
                    clearInterval(intervalID);
                    if (isBound === true) {
                        LoadSaveService.retrieveSubmission(quoteRetrievalDTO).then((boundSubmission) => {
                            return resolve(boundSubmission);
                        });
                    }

                    if (retryCount < counter) {
                        return reject();
                    }
                    counter += 1;
                    if (!isBound && counter < retryCount) {
                        // eslint-disable-next-line no-use-before-define
                        intervalID = setInterval(isPolicyBound, timeoutSequence[counter] * 1000);
                    }
                });
            };
            let intervalID = setInterval(isPolicyBound, timeoutSequence[counter] * 1000);
        });
        const instrumentPromise = initSelectedRatingPaymentMethod();
        showProgressDialogTwoPromises(platformMessages.loading, promise, instrumentPromise);

        return promise;
    }, []);

    useEffect(() => {
        const updateSubmissionData = async () => {
            try {
                const boundSubmission = await updateSubmisionDataIfNotBound(submissionVM);
                const boundSubmissionVM = viewModelService.create(boundSubmission, 'pc', 'edge.capabilities.quote.submission.dto.QuoteDataDTO');
                updateWizardData(boundSubmissionVM);
            } catch (error) {
                goToContactUs();
            }
        };
        setTimeout(updateSubmissionData, 200);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isPersonalAuto = useCallback(() => {
        return MLProductCodeUtil.isPersonalAuto(baseData.productCode);
    }, [baseData.productCode]);

    const isHomeowners = useCallback(() => {
        return MLProductCodeUtil.isHomeOwners(baseData.productCode);
    }, [baseData.productCode]);

    const printConfirmation = () => {
        window.print();
    };

    const onCreateAccount = () => {  
        const cssHomeUrl = sessionStorage.getItem('CSSHomeUrl');  
        window.open(cssHomeUrl, 'MsgWindow');        
    };

    const isCarcoContingencyAvailable = () => {
        return openContingencies.some((contingency) => contingency.title === CARCO_CONTINGENCY_TITLE);
    };

    const areAnyContingencies = () => {
        return openContingencies.length > 0;
    };

    const findCarcoLocations = () => {
        window.open(ML_CARCO_FIND_LOCATION_URL, 'MsgWindow');
    };

    const isPaymentMethodCreditCardOrBankAccount = useCallback((isDownPayment) => {
        if (isDownPayment) {
            return bindData.paymentDetails.downPaymentMethod_ML === 'creditcard' || bindData.paymentDetails.downPaymentMethod_ML === 'ach';
        }
        return bindData.paymentDetails.ratingPaymentMethod_ML === 'credit_debit_card' || bindData.paymentDetails.ratingPaymentMethod_ML === 'bank_account';
    }, [bindData.paymentDetails.downPaymentMethod_ML, bindData.paymentDetails.ratingPaymentMethod_ML]);

    const paymentMethodContent = useCallback((paymentMethod, accountNumber, isDownPayment = false) => {
        return (
            <>
                {paymentMethod && <span>{paymentMethod}</span>}
                &nbsp;
                {isPaymentMethodCreditCardOrBankAccount(isDownPayment) && (
                    <span>{translator(messages.endingInXXX, { accountNumber: accountNumber })}</span>
                )}
            </>
        );
    }, [isPaymentMethodCreditCardOrBankAccount, translator]);

    const formatAmountString = useCallback((amount) => {
        try {
            return intl.formatNumber(amount.amount, { style: 'currency', currency: amount.currency });
        } catch (e) {
            return '';
        }
    }, [intl]);

    const isCarcoContingency = (contingency) => {
        return contingency.title === CARCO_CONTINGENCY_TITLE;
    };


    const generateOverrides = () => {
        const openContingenciesOverride = openContingencies.map((openContingency, index) => {
            return {
                [`contigencyTitle${index}`]: {
                    content: openContingency.title,
                    className: 'confirmationListItem'.concat(isCarcoContingency(openContingency) ? ' font-weight-bold' : '')
                }
            };
        });

        const vehicleDetailsOverride = vehicles.map((vehicle, index) => {
            return {
                [`vehicleDetails${index}`]: {
                    content: (
                        <>
                            <strong>
                                {vehicle.year}
                                &nbsp;
                                {_.capitalize(vehicle.make)}
                            </strong>
                            &nbsp;
                            {_.capitalize(vehicle.model)}
                            &nbsp;
                            {vehicle.trim_ML}
                        </>
                    )
                }
            };
        });

        return Object.assign({}, ...openContingenciesOverride, ...vehicleDetailsOverride);
    };

    const resolvers = {
        resolveCallbackMap: {
            printConfirmation: printConfirmation,
            onCreateAccount: onCreateAccount,
            findCarcoLocations: findCarcoLocations
        },
        resolveComponentMap: {
            namedNonOwnerTooltipComponent: NamedNonOwnerTooltipComponent,
            MLAlertGroupComponent
        },
        resolveClassNameMap: styles
    };

    const convertLocalDateToDate = (localDate) => {
        const parsedDate = new Date(localDate.year, localDate.month, localDate.day);
        return moment(parsedDate).format('MM/DD/YYYY');
    };


    const overrides = {
        policyNumber: {
            content: translator(messages.policyNumberMessage, { policyNumber: policyNumber })
        },
        emailSent: {
            content: translator(messages.policyInfoSentByEmail, { emailAddress: email }),
            className: isPersonalAuto() ? 'body-copy font-weight-bold light vertical-spacing-10' : 'body-copy font-weight-bold light vertical-spacing-30'
        },
        viewPrintIDAndDocs: {
            visible: isPersonalAuto()
        },
        viewPrintDocs: {
            visible: !isPersonalAuto()
        },
        reminderList: {
            className: isCarcoContingencyAvailable() && areAnyContingencies() ? 'styled-ul-list-basic no-inner-margin vertical-spacing-20' : 'styled-ul-list-basic no-inner-margin'
        },
        openContingenciesIterable: {
            visible: areAnyContingencies(),
            data: openContingencies
        },
        yourPolicyWithProduct: {
            content: translator(messages.yourPolicyWithProduct, { productName: getProductName() })
        },
        policyDetailsPolicyNumber: {
            content: translator(messages.policyDetailsPolicyNumber, { policyNumber: policyNumber })
        },
        effectiveDate: {
            content: translator(messages.periodStartDate, { effectiveDate: convertLocalDateToDate(baseData.periodStartDate) })
        },
        expirationDate: {
            content: translator(messages.periodExpirationDate, { expirationDate: convertLocalDateToDate(baseData.periodEndDate) })
        },
        selectedPaymentMethod: {
            content: paymentMethodContent(selectedPaymentMethod, last4Digits)
        },
        paymentDetailsContainer: {
            visible: !!paymentTotal.amount && paymentTotal.amount > 0
        },
        paymentAmount: {
            content: formatAmountString(paymentTotal)
        },
        paymentDate: {
            content: paymentReceivedDate
        },
        paymentReceivedDateContainer: {
            visible: !!paymentReceivedDate
        },
        // confirmationNumberContainer: {
        //     visible: !!confirmationNumber
        // },
        confirmationContainer: {
            className: isQnB ? "row mobile-width" : "row"
        },
        confirmationNumber: {
            content: confirmationNumber
        },
        paymentMethod: {
            content: paymentMethodContent(downPaymentMethod, downPaymentLast4Digits, true)
        },
        primaryInsuredFullName: {
            content: primaryNamedInsuredFullName
        },
        addressLine1: {
            content: addressLine1
        },
        addressLine2: {
            visible: !!addressLine2,
            content: addressLine2
        },
        cityStateZip: {
            content: `${city}, ${state} ${postalCode}`
        },
        coInsured: {
            visible: !!coInsured
        },
        coInsuredFullName: {
            visible: !!coInsured,
            content: coInsuredFullName
        },
        homeOwnersPropertyContainer: {
            visible: isHomeowners()
        },
        propertyAddressLine1: {
            content: propertyAddressLine1
        },
        propertyAddressLine2: {
            visible: !!propertyAddressLine2,
            content: propertyAddressLine2
        },
        propertyCityStateZip: {
            content: `${propertyAddressCity}, ${propertyAddressState} ${propertyAddressZip}`
        },
        personalAutoVehicleContainer: {
            visible: vehicles.length > 0 && isPersonalAuto()
        },
        isNamedNonOwnerContainer: {
            visible: isNamedNonOwner
        },
        vehicleDetailsContainer: {
            visible: !isNamedNonOwner
        },
        vehiceleDetailsIterable: {
            data: vehicles
        },
        findCarcoButton: {
            visible: isCarcoContingencyAvailable() && areAnyContingencies()
        },
        idCardInfo: {
            visible: isPersonalAuto()
        },
        purchaseHeader: {
            className: isQnB ? "page-header vertical-spacing-20 headerOne" : "page-header vertical-spacing-20"
        },
        createAccountButotn: {
            mlTagManagerLinkClick: "Create Account",
            className: isQnB ? "primaryButton widthSmall" : "",
            content: isQnB ? convertToSentenceCase(translator(messages.createAccount)) : translator(messages.createAccount)
        },
        printSummaryButton: {
            mlTagManagerLinkClick: "Print Confirmation",
            className: isQnB ? "vertical-spacing-10 color-link secondaryButton widthSmall" : "vertical-spacing-10",
            content: isQnB ? convertToSentenceCase(translator(messages.printConfirmation)) : translator(messages.printConfirmation)
        },
        confirmationPageContainer: {
            className: isQnB ? "confirmationPageContainerQnB page-content-qb" : "confirmationPageContainer page-content-qb"
        },
        ...generateOverrides()
    };

    return <MetadataContent uiProps={metadata.pageContent} overrideProps={overrides} {...resolvers} />;
}

ConfirmationPage.propTypes = wizardProps;
export default ConfirmationPage;
